import Vue from 'vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import tr from '@/libs/i18n/locales/tr-TR.json'
import {
  fetchSubscriptionStanding,
} from '@/libs/subscriptionStanding'

export const ActionRequirement = Object.freeze({
  SUBSCRIPTION_ONLY: 'subscription-only',
  SUBSCRIPTION_AND_STANDING: 'subscription-and-standing',
})

export const SubscriptionActionOutcome = Object.freeze({
  SUBSCRIPTION_REQUIRED: 'subscription-required',
  APPROVAL_MISSING: 'approval-missing',
  APPROVAL_REJECTED: 'approval-rejected',
  APPROVAL_DENIED: 'approval-denied',
  ACCOUNT_BLOCKED: 'account-blocked',
  STATUS_UNAVAILABLE: 'status-unavailable',
})

export const SUBSCRIPTION_REQUIRED_TEXT = 'Bu işlem için abone olmalısınız.'

const OUTCOME_DEDUPE_MS = 1500
let lastPresentedOutcome = null
let lastPresentedAt = 0

const outcomePresentation = (outcome, reason) => {
  if (outcome === SubscriptionActionOutcome.SUBSCRIPTION_REQUIRED && reason === 'free-access-ended') {
    return { title: tr.freeAccess.endedTitle, text: tr.freeAccess.endedGuard, eventName: 'diko:subscription-required' }
  }
  switch (outcome) {
    case SubscriptionActionOutcome.SUBSCRIPTION_REQUIRED:
      return {
        title: 'Abonelik Bulunamadı',
        text: SUBSCRIPTION_REQUIRED_TEXT,
        eventName: 'diko:subscription-required',
      }
    case SubscriptionActionOutcome.APPROVAL_MISSING:
      return {
        title: 'Bilgilerinizi İncelemeye Gönderin',
        text: 'Yeni bir çalışma başlatmak için hesap bilgilerinizi incelemeye göndermeniz gerekiyor.',
        eventName: 'diko:approval-required',
      }
    case SubscriptionActionOutcome.APPROVAL_REJECTED:
      return {
        title: 'Başvuru Sonucunuzu İnceleyin',
        text: 'Başvuru sonucunuzu ve açıklamayı hesap bilgilerinizden inceleyebilirsiniz.',
        eventName: 'diko:approval-required',
      }
    case SubscriptionActionOutcome.ACCOUNT_BLOCKED:
      return {
        title: 'Hesabınız İçin Destek Gerekiyor',
        text: 'Hesabınızla ilgili bu işlemi tamamlamak için destek ekibimizle iletişime geçin.',
      }
    case SubscriptionActionOutcome.APPROVAL_DENIED:
      return {
        title: 'Hesap Durumu',
        text: 'Hesap durumunuz şu anda bu işleme izin vermiyor. Lütfen daha sonra tekrar deneyin.',
      }
    case SubscriptionActionOutcome.STATUS_UNAVAILABLE:
    default:
      return {
        title: 'Bilgiler Güncellenemedi',
        text: 'Hesap ve abonelik durumunuz doğrulanamadı. Lütfen tekrar deneyin.',
      }
  }
}

const dispatchOutcomeEvent = (eventName, detail) => {
  if (!eventName
    || typeof window === 'undefined'
    || typeof window.dispatchEvent !== 'function') return
  window.dispatchEvent(new CustomEvent(eventName, { detail }))
}

// Shared by local preflight and the JWT response interceptor. This is what
// prevents a double click or a request race from producing duplicate toast
// and navigation outcomes.
export const presentSubscriptionActionOutcome = ({
  outcome,
  errorCode,
  reason,
  source,
  suppressToast = false,
} = {}) => {
  const presentation = outcomePresentation(outcome, reason)
  const now = Date.now()
  const dedupeKey = `${outcome || SubscriptionActionOutcome.STATUS_UNAVAILABLE}:${reason || ''}`

  if (lastPresentedOutcome === dedupeKey && now - lastPresentedAt < OUTCOME_DEDUPE_MS) {
    return false
  }
  lastPresentedOutcome = dedupeKey
  lastPresentedAt = now

  if (!suppressToast && Vue.$toast) {
    Vue.$toast({
      component: ToastificationContent,
      props: {
        title: presentation.title,
        text: presentation.text,
        icon: 'AlertTriangleIcon',
        variant: 'danger',
        timeout: 2000,
      },
    })
  }

  const detail = {}
  if (errorCode != null) detail.errorCode = errorCode
  if (source) detail.source = source
  if ([
    SubscriptionActionOutcome.APPROVAL_MISSING,
    SubscriptionActionOutcome.APPROVAL_REJECTED,
  ].includes(outcome)) detail.reason = outcome
  dispatchOutcomeEvent(presentation.eventName, detail)
  return true
}

export const resetSubscriptionActionOutcomeDedupe = () => {
  lastPresentedOutcome = null
  lastPresentedAt = 0
}

const deny = ({
  outcome, source, standing = null, error = null,
}) => {
  presentSubscriptionActionOutcome({ outcome, source, reason: standing && standing.accessReason })
  return {
    allowed: false,
    reason: outcome,
    standing,
    error,
  }
}

export const guardSubscriptionAction = async ({
  requirement,
  source,
  forceRefresh = false,
} = {}) => {
  if (!Object.values(ActionRequirement).includes(requirement)) {
    return deny({
      outcome: SubscriptionActionOutcome.STATUS_UNAVAILABLE,
      source,
      error: new Error('Unknown subscription action requirement'),
    })
  }

  let standing
  try {
    standing = await fetchSubscriptionStanding({ forceRefresh })
  } catch (error) {
    return deny({
      outcome: SubscriptionActionOutcome.STATUS_UNAVAILABLE,
      source,
      error,
    })
  }

  if (!standing || standing.contractComplete !== true) {
    return deny({
      outcome: SubscriptionActionOutcome.STATUS_UNAVAILABLE,
      source,
      standing,
    })
  }

  // Match the server's 402 precedence even when application standing is also
  // missing/rejected. A user without access always receives billing guidance.
  if (standing.hasAccess !== true) {
    return deny({
      outcome: SubscriptionActionOutcome.SUBSCRIPTION_REQUIRED,
      source,
      standing,
    })
  }

  if (requirement === ActionRequirement.SUBSCRIPTION_ONLY) {
    return { allowed: true, reason: null, standing }
  }

  if (standing.approvalStanding.allowed === true) {
    return { allowed: true, reason: null, standing }
  }

  const approvalReason = standing.approvalStanding.reason
  if (approvalReason === SubscriptionActionOutcome.APPROVAL_MISSING) {
    return deny({
      outcome: SubscriptionActionOutcome.APPROVAL_MISSING,
      source,
      standing,
    })
  }
  if (approvalReason === SubscriptionActionOutcome.APPROVAL_REJECTED) {
    return deny({
      outcome: SubscriptionActionOutcome.APPROVAL_REJECTED,
      source,
      standing,
    })
  }

  return deny({
    outcome: SubscriptionActionOutcome.APPROVAL_DENIED,
    source,
    standing,
  })
}

export default guardSubscriptionAction
